import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { Button, StatusBadge, BackButton } from '../components/common';
import { SeasonProvider } from '../context/SeasonContext';
import { Heading, Text, Typography } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { APP_PAGE_BLEED_ROOT, APP_PAGE_GUTTER } from '../utils/sessionMobileLayout';
import {
  ManagementSection,
  ManagementActionRow,
} from '../components/clubs/ManagementHubSection';
import ClubLogo from '../components/clubs/ClubLogo';

const ClubManagement = ({ currentUser, embedded = false }) => {
  const { clubId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get activeTab and navigation source from navigation state
  const activeTab = location.state?.activeTab;
  const fromHomepage = location.state?.fromHomepage;
  const fromManageClubs = location.state?.fromManageClubs;
  const fromMyClubs = location.state?.fromMyClubs;

  useEffect(() => {
    if (clubId && currentUser?.id) {
      fetchClubDetails();
    }
  }, [clubId, currentUser?.id]);

  // Auto-navigate to specific management area if activeTab is provided
  useEffect(() => {
    if (activeTab && club && userRole) {
      switch (activeTab) {
        case 'session-management':
          handleManageSessions();
          break;
        case 'player-management':
          handleClubAdmin();
          break;
        default:
          break;
      }
    }
  }, [activeTab, club, userRole]);

  const fetchClubDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get club details
      const { data: clubData, error: clubError } = await supabase
        .from('clubs')
        .select('*')
        .eq('id', clubId)
        .single();

      if (clubError) throw clubError;

      // Get user's role in this club
      const { data: roleData, error: roleError } = await supabase
        .from('players_club')
        .select('club_role')
        .eq('player_id', currentUser.id)
        .eq('club_id', clubId)
        .single();

      if (roleError) throw roleError;

      // Check if user has management permissions
      if (!['host', 'admin', 'owner'].includes(roleData.club_role)) {
        throw new Error('You do not have permission to manage this club');
      }

      setClub(clubData);
      setUserRole(roleData.club_role);
    } catch (err) {
      console.error('Error fetching club details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSessions = () => {
    navigate(`/club/${clubId}/sessions`, {
      state: {
        fromMyClubs: fromMyClubs,
        fromHomepage: fromHomepage,
        fromManageClubs: fromManageClubs,
        returnToDashboard: true
      }
    });
  };

  const handleTournaments = () => {
    navigate(`/club/${clubId}/tournaments`, {
      state: {
        fromMyClubs: fromMyClubs,
        fromHomepage: fromHomepage,
        fromManageClubs: fromManageClubs,
        returnToDashboard: true
      }
    });
  };

  const handleClubAdmin = () => {
    navigate(`/club/${clubId}/settings`, {
      state: {
        fromMyClubs: fromMyClubs,
        fromHomepage: fromHomepage,
        fromManageClubs: fromManageClubs,
        returnToDashboard: true
      }
    });
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'owner':
        return 'info';
      case 'admin':
        return 'primary';
      case 'host':
        return 'success';
      default:
        return 'default';
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'owner':
        return 'Owner';
      case 'admin':
        return 'Admin';
      case 'host':
        return 'Host';
      default:
        return role;
    }
  };

  const canViewClubSettings = userRole === 'owner' || userRole === 'admin';

  const backLabel = clubId
    ? 'Back to Club Dashboard'
    : fromHomepage
      ? 'Back to Home'
      : fromMyClubs || fromManageClubs
        ? 'Back to My Clubs'
        : 'Back to My Clubs';

  const handleBack = () => {
    if (clubId) {
      navigate(`/clubs/${clubId}`, { state: { fromMyClubs: fromMyClubs || fromManageClubs } });
    } else if (fromHomepage) {
      navigate('/');
    } else {
      navigate('/my-clubs');
    }
  };

  const navState = {
    fromMyClubs,
    fromHomepage,
    fromManageClubs,
    returnToDashboard: true,
  };

  if (loading) {
    return (
      <div className={`min-h-[50vh] bg-background ${embedded ? '' : APP_PAGE_BLEED_ROOT}`}>
        <div className={`mx-auto max-w-app-shell ${embedded ? 'px-0 py-6' : `${APP_PAGE_GUTTER} py-12`}`}>
          <div className="text-center">
            <Typography variant="h4" className="mb-2 text-text-primary">
              Loading...
            </Typography>
            <Typography variant="muted">Loading club management options</Typography>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-[50vh] bg-background ${embedded ? '' : APP_PAGE_BLEED_ROOT}`}>
        <div className={`mx-auto max-w-app-shell ${embedded ? 'px-0 py-6' : `${APP_PAGE_GUTTER} py-12`}`}>
          <Card variant="default" size="medium" className="text-center">
            <Typography variant="h4" className="mb-2 text-text-primary">
              Error
            </Typography>
            <Typography variant="muted" className="mb-4">
              {error}
            </Typography>
            <Button onClick={() => navigate('/my-clubs')} variant="outline">
              Back to My Clubs
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const pageShellClass = embedded
    ? ''
    : `${APP_PAGE_BLEED_ROOT} min-h-screen bg-background`;
  const contentGutterClass = embedded ? '' : `${APP_PAGE_GUTTER} pt-3 pb-8 md:pt-6`;

  return (
    <SeasonProvider clubId={clubId}>
      <div className={pageShellClass}>
        <div className={`mx-auto max-w-app-shell ${contentGutterClass}`}>
          {embedded ? (
            <div className="mb-6">
              <Heading level={2} className="mb-1 text-text-primary">
                Club Management
              </Heading>
              <Text className="text-sm text-text-secondary">
                Manage sessions, settings, and administrative tasks
              </Text>
            </div>
          ) : (
            <div className="mb-6 sm:mb-8">
              <BackButton hideOnDesktop className="mb-2" onClick={handleBack}>
                {backLabel}
              </BackButton>
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-3">
                  <ClubLogo
                    logoImageUrl={club.logo_image_url}
                    clubName={club.name}
                    size="sm"
                    className="border border-border-medium shadow-brand-sm"
                  />
                  <Heading level={1} className="min-w-0 truncate text-xl font-bold text-text-primary sm:text-2xl">
                    {club.name}
                  </Heading>
                  <StatusBadge variant={getRoleBadgeVariant(userRole)} size="small" className="shrink-0">
                    {getRoleDisplayName(userRole)}
                  </StatusBadge>
                </div>
                <Text className="mt-1 text-sm text-text-secondary">
                  Manage sessions, settings and administrative tasks for your club
                </Text>
              </div>
            </div>
          )}

          <div className="space-y-6 sm:space-y-8">
            <ManagementSection
              iconType="sessions"
              title="Sessions"
              subtitle="Start, plan, and wrap up"
            >
              <ManagementActionRow
                iconType="sessions"
                title="Start session"
                description="Start now, or start a session you already planned"
                onClick={() => navigate(`/club/${clubId}/sessions/start`, { state: navState })}
              />
              <ManagementActionRow
                iconType="planning"
                title="Plan sessions"
                description="Create and manage upcoming sessions"
                onClick={handleManageSessions}
              />
              <ManagementActionRow
                iconType="today-work"
                title="Review sessions"
                description="Wrap up payments and archive past sessions"
                onClick={() => navigate(`/club/${clubId}/sessions/review`, { state: navState })}
              />
            </ManagementSection>

            <ManagementSection
              iconType="tournaments"
              title="Tournaments"
              subtitle="Create and manage events"
            >
              <ManagementActionRow
                iconType="tournaments"
                title="Tournaments"
                description="Create and manage tournaments"
                onClick={handleTournaments}
              />
            </ManagementSection>

            <ManagementSection
              iconType="performance"
              title="Club Analytics"
              subtitle="Metrics and tools"
            >
              <ManagementActionRow
                iconType="performance"
                title="Club Analysis"
                description="Stats and trends"
                onClick={() =>
                  navigate('/club-analysis', {
                    state: {
                      selectedClub: club,
                      userRole,
                    },
                  })
                }
              />
              <ManagementActionRow
                iconType="payments"
                title={
                  <span className="inline-flex flex-wrap items-center gap-2">
                    <span>Payment Tracking</span>
                    <StatusBadge variant="info" size="small" className="!px-1.5 !py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                      [BETA]
                    </StatusBadge>
                  </span>
                }
                description="Balances and pricing"
                onClick={() => navigate(`/club/${clubId}/payment-tracking`, { state: navState })}
              />
            </ManagementSection>

            <ManagementSection
              iconType="league"
              title="League"
              subtitle="County club link, squad, and player review"
            >
              <ManagementActionRow
                iconType="league"
                title="League squad"
                description="Link this club to Middlesex, then match player names"
                onClick={() => navigate(`/club/${clubId}/league-squad`, { state: navState })}
              />
              <ManagementActionRow
                iconType="performance"
                title="Player performance"
                description="Review rubber records for any county team"
                onClick={() => navigate(`/club/${clubId}/league-performance`, { state: navState })}
              />
            </ManagementSection>

            {canViewClubSettings && (
              <ManagementSection
                iconType="settings"
                title="Settings"
                subtitle="Members and configuration"
              >
                <ManagementActionRow
                  iconType="matchups"
                  title="Player Management"
                  description="Members, guests, and requests"
                  onClick={handleClubAdmin}
                />
                <ManagementActionRow
                  iconType="settings"
                  title="Club Settings"
                  description="Club profile and details"
                  onClick={() => navigate(`/club/${clubId}/club-settings`, { state: navState })}
                />
              </ManagementSection>
            )}
          </div>
        </div>
      </div>
    </SeasonProvider>
  );
};

export default ClubManagement;
